import { __ } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

/**
 * Helper: flatten a WPGraphQL connection to a comma-separated string.
 * Taxonomy pick fields come back as { nodes: [{ name }] }.
 * @param connection
 */
const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( ', ' ) ?? '';

/**
 * Helper: flatten a WPGraphQL post-type relationship connection to a comma-separated string.
 * Post type pick fields come back as { nodes: [{ title }] }.
 * @param connection
 */
const postTitles = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.title ).join( ', ' ) ?? '';

/**
 * filterFn for taxonomy columns: checks whether the selected term name appears
 * in the raw WPGraphQL connection stored on row.original[columnId].
 * @param row
 * @param columnId
 * @param filterValue
 */
function taxFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.name === filterValue ) ?? false;
}
taxFilterFn.autoRemove = ( val ) => ! val;

/**
 * filterFn for post-type relationship columns: checks whether the selected
 * post title appears in the raw WPGraphQL connection stored on row.original[columnId].
 * @param row
 * @param columnId
 * @param filterValue
 */
function postRefFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.title === filterValue ) ?? false;
}
postRefFilterFn.autoRemove = ( val ) => ! val;

/**
 * Column definitions for the project CPT.
 *
 * Field names follow WPGraphQL for Pods convention: camelCase, no leading underscore.
 * Verify against /graphql schema explorer if any field returns undefined.
 * @param root0
 * @param root0.onDelete
 */
export function projectColumns( { onDelete } ) {
	return [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this project to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( 'title', {
			header: __( 'Project', 'anacrm' ),
			cell: ( { row, getValue } ) => (
				<a
					href={ row.original.uri }
					className="font-semibold text-blue hover:!no-underline"
				>
					{ getValue() }
				</a>
			),
		} ),

		col.accessor( ( row ) => taxNames( row.territories ), {
			id: 'territories',
			header: __( 'Territory', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.regions ), {
			id: 'regions',
			header: __( 'Region', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.cities ), {
			id: 'cities',
			header: __( 'Municipality', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => postTitles( row.orgRef ), {
			id: 'orgRef',
			header: __( 'Organisation', 'anacrm' ),
			filterFn: postRefFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.estados ), {
			id: 'estados',
			header: __( 'Status', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( 'contactMail', {
			header: __( 'Email', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				if ( ! v ) {
					return '—';
				}
				const emails = v
					.split( ',' )
					.map( ( e ) => e.trim() )
					.filter( Boolean );
				return emails.map( ( email, i ) => (
					<a
						key={ i }
						href={ `mailto:${ email }` }
						className="text-blue hover:!no-underline block"
					>
						{ email }
					</a>
				) );
			},
		} ),

		col.accessor( 'contactWebsite', {
			header: __( 'Web', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				return v ? (
					<a
						href={ v }
						target="_blank"
						rel="noopener noreferrer"
						className="text-blue hover:!no-underline truncate block max-w-[160px]"
					>
						{ v }
					</a>
				) : (
					'—'
				);
			},
		} ),
	];
}
